import styles from './AllImplantsList.module.css';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import {
    allImplantsGetImplantsByManufacturer,
    allImplantsGetImplantsByType,
    allImplantsGetImplantsByBodyPlacement,
    allImplantsGetManufacturers,
    allImplantsGetTypes,
    allImplantsGetBodyPlacements,
} from '../../services/apiService';
import { toTitleCase } from '../../utils/utils';
import LoadingRing from '../ui-components/LoadingRing';

export interface AllImplantsListRef {
    scrollToLetter: (letter: string) => void;
}

const AllImplantsList = forwardRef<
    AllImplantsListRef,
    {
        category: '' | 'types' | 'manufacturers' | 'bodyPlacements';
        defaultValue: string;
        onAvailableLettersChange: (letters: string[]) => void;
    }
>(({ category, defaultValue, onAvailableLettersChange }, ref) => {
    const [types, setTypes] = useState<string[]>([]);
    const [manufacturers, setManufacturers] = useState<string[]>([]);
    const [bodyPlacements, setBodyPlacements] = useState<string[]>([]);

    const [selectedCat, setSelectedCat] = useState('');
    const [implants, setImplants] = useState<any[]>([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [isLoadingImplants, setIsLoadingImplants] = useState(false);

    const selRef = useRef<HTMLLIElement | null>(null);
    const itemsRef = useRef<Map<string, HTMLLIElement>>(new Map());

    useImperativeHandle(
        ref,
        () => ({
            scrollToLetter: (letter: string) => {
                const char = letter.toUpperCase();
                let list: string[] = [];
                if (category === 'types') list = types;
                else if (category === 'manufacturers') list = manufacturers;
                else if (category === 'bodyPlacements') list = bodyPlacements;

                const match = list.find((item) =>
                    item.toUpperCase().startsWith(char)
                );

                if (match) {
                    const node = itemsRef.current.get(match);
                    if (node) {
                        node.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }
                }
            },
        }),
        [category, types, manufacturers, bodyPlacements]
    );

    const selectManufacturer = async (mId: string) => {
        setSelectedCat(mId);
        setIsLoadingImplants(true);
        try {
            const imps = await allImplantsGetImplantsByManufacturer(mId);
            setImplants(imps);
        } finally {
            setIsLoadingImplants(false);
        }
    };

    const selectType = async (t: string) => {
        setSelectedCat(t);
        setIsLoadingImplants(true);
        try {
            const imps = await allImplantsGetImplantsByType(t);
            setImplants(imps);
        } finally {
            setIsLoadingImplants(false);
        }
    };

    const selectBodyPlacement = async (bp: string) => {
        setSelectedCat(bp);
        setIsLoadingImplants(true);
        try {
            const imps = await allImplantsGetImplantsByBodyPlacement(bp);
            setImplants(imps);
        } finally {
            setIsLoadingImplants(false);
        }
    };

    const getData = async () => {
        setIsLoadingCategories(true);
        try {
            if (category === 'types') {
                const t = await allImplantsGetTypes();
                setTypes(t);
            }
            if (category === 'manufacturers') {
                const m = await allImplantsGetManufacturers();
                setManufacturers(m);
            }
            if (category === 'bodyPlacements') {
                const bp = await allImplantsGetBodyPlacements();
                setBodyPlacements(bp);
            }
        } finally {
            setIsLoadingCategories(false);
        }
    };

    useEffect(() => {
        let list: string[] = [];
        if (category === 'types') list = types;
        else if (category === 'manufacturers') list = manufacturers;
        else if (category === 'bodyPlacements') list = bodyPlacements;

        const letters = Array.from(
            new Set(
                list
                    .map((item) => item.charAt(0).toUpperCase())
                    .filter((char) => /[A-Z]/.test(char))
            )
        ).sort();
        onAvailableLettersChange(letters);
    }, [category, types, manufacturers, bodyPlacements]);

    useEffect(() => {
        getData();
    }, [category]);

    useEffect(() => {
        if (category === 'types') {
            selectType(defaultValue);
        }
        if (category === 'manufacturers') {
            selectManufacturer(defaultValue);
        }
        if (category === 'bodyPlacements') {
            selectBodyPlacement(defaultValue);
        }
    }, [defaultValue]);

    useEffect(() => {
        console.log('ref', selRef);
        if (selRef.current) {
            selRef.current.scrollIntoView();
        }
    }, [types, bodyPlacements]);

    const openImplant = (id: string) => {
        let param = '';
        if (category === 'types') {
            param = 'type';
        } else if (category === 'manufacturers') {
            param = 'manufacturer';
        } else if (category === 'bodyPlacements') {
            param = 'bodyPlacement';
        }
        window.history.pushState(
            {},
            '',
            `/all?${param}=${encodeURIComponent(selectedCat)}`
        );
        window.location.href = '/implant/' + id;
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.category}>
                {isLoadingCategories && (
                    <div className={styles.loading}>
                        <LoadingRing size="large" />
                    </div>
                )}
                {!isLoadingCategories && category === 'types' && (
                    <ul>
                        {types.map((t) => (
                            <li
                                ref={(node) => {
                                    if (node) itemsRef.current.set(t, node);
                                    else itemsRef.current.delete(t);
                                    if (selectedCat === t) selRef.current = node;
                                }}
                                className={
                                    selectedCat === t ? styles.selected : ''
                                }
                                onClick={() => selectType(t)}
                            >
                                {t}
                            </li>
                        ))}
                    </ul>
                )}
                {!isLoadingCategories && category === 'manufacturers' && (
                    <ul>
                        {manufacturers.map((m) => (
                            <li
                                ref={(node) => {
                                    if (node) itemsRef.current.set(m, node);
                                    else itemsRef.current.delete(m);
                                    if (selectedCat === m) selRef.current = node;
                                }}
                                className={
                                    selectedCat === m ? styles.selected : ''
                                }
                                onClick={() => selectManufacturer(m)}
                            >
                                {toTitleCase(m)}
                            </li>
                        ))}
                    </ul>
                )}
                {!isLoadingCategories && category === 'bodyPlacements' && (
                    <ul>
                        {bodyPlacements.map((bp) => (
                            <li
                                ref={(node) => {
                                    if (node) itemsRef.current.set(bp, node);
                                    else itemsRef.current.delete(bp);
                                    if (selectedCat === bp) selRef.current = node;
                                }}
                                className={
                                    selectedCat === bp ? styles.selected : ''
                                }
                                onClick={() => selectBodyPlacement(bp)}
                            >
                                {toTitleCase(bp)}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className={styles.implants}>
                {isLoadingImplants && (
                    <div className={styles.loading}>
                        <LoadingRing size="large" />
                    </div>
                )}
                {!isLoadingImplants && (
                    <ul>
                        {implants.map((i) => (
                            <li onClick={() => openImplant(i._id)}>
                                {i.Model}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
});

export default AllImplantsList;
